import { Injectable, OnDestroy } from '@angular/core';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { EMDASH } from '@products/utils/constants/product.constants';
import { fromLocaleNumber } from '@utils/il8n/number-il8n';
import { Logger } from '@utils/logger';
import unzip from 'lodash/unzip';
import { Observable, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import {
  CellObject,
  ColInfo,
  Comments,
  ExcelDataType,
  Hyperlink,
  NumberFormat,
} from 'xlsx/types';

const logger = Logger.getLogger('ExcelDownloadService');

/**
 * This interface extends the CellObject interface to add optional meta data
 * TODO: For some reason, the `export interface FtCellObject extends CellObject {...}` method
 * isn't compiling. Ideally this can be fixed
 * For now this is just a manual copy of CellObject interface with `data` added
 */
export interface FtCellObject {
  // allows us to add meta data to cell e.g. colId
  data?: any;

  /** The raw value of the cell.  Can be omitted if a formula is specified */
  v?: string | number | boolean | Date;

  /** Formatted text (if applicable) */
  w?: string;

  /**
   * The Excel Data Type of the cell.
   * b Boolean, n Number, e Error, s String, d Date, z Empty
   */
  t: ExcelDataType;

  /** Cell formula (if applicable) */
  f?: string;

  /** Range of enclosing array if formula is array formula (if applicable) */
  F?: string;

  /** Rich text encoding (if applicable) */
  r?: any;

  /** HTML rendering of the rich text (if applicable) */
  h?: string;

  /** Comments associated with the cell */
  c?: Comments;

  /** Number format string associated with the cell (if requested) */
  z?: NumberFormat;

  /** Cell hyperlink object (.Target holds link, .tooltip is tooltip) */
  l?: Hyperlink;

  /** The style/theme of the cell (if applicable) */
  s?: any;
}

// used to add space between sections
export const EMPTY_ROW = [[]];

export const EMPTY_CELL: CellObject = { v: '', t: 'z' };

export const EMDASH_CELL: CellObject = { v: EMDASH, t: 's' };

/**
 * Builds a CellObject from raw data
 * @param val value for the cell
 * @param dataType if not set, code will work out if this is string 's', number 'n', date 'd', or empty 'z'
 * @param linkUrl if set, will turn cell into a link. Needs to be full path - relative url won't work
 * @param linkTooltip text to appear when hovering over link
 * @param comments cell comment
 * @param data thids allows any meta data to be attached to the cell object
 * NB: in future, cell styling can be added if/when we purchase PRO version
 * @returns FtCellObject for a value with appropriate props set
 */
export const getCellObject = (
  val: string,
  dataType?: ExcelDataType,
  linkUrl?: string,
  linkTooltip?: string,
  comments?: string,
  data?: any
): FtCellObject => {
  let v: string | number | boolean | Date = val;
  let t: ExcelDataType = dataType;

  if (dataType) {
    // if dataType passed in, set value accordingly
    if (dataType === 'n') {
      v = fromLocaleNumber(val);
    }
  } else {
    // otherwise default type to string
    t = 's';

    // see if number can be parsed from val
    const numVal: number = fromLocaleNumber(val);
    if (numVal !== null) {
      t = 'n';
      v = numVal;
    }
  }

  // regardless of type, check if cell is empty
  if (v === undefined || v === null || v === EMDASH) {
    t = 's';
    v = EMDASH;
  }

  // set cell minimum props
  const cell: FtCellObject = {
    t, // set type
    v,
  };

  // add link props if set
  if (linkUrl) {
    const link: Hyperlink = { Target: linkUrl };
    if (linkTooltip) {
      link.Tooltip = linkTooltip;
    }
    cell.l = link;
  }

  // add cell comments if set
  if (comments) {
    const c: Comments = [{ t: comments }];
    // NB: this looks odd (setting a prop on an array), but seems to be the way type is defined
    c.hidden = true;
    cell.c = c;
  }

  // add optional cell metaData
  if (data) {
    cell.data = data;
  }

  return cell;
};

/**
 * @param dataCells cells to calculate content of
 * @param minWidth (default) minimum width in characters for columns
 * @returns an array of column widths optimised for cell contents
 */
export const getColWidths = (
  dataCells: FtCellObject[][],
  minWidth = 10
): ColInfo[] => {
  // unzip swaps the 2D array's dimensions
  // i.e. rows[columns] --> columns[rows]
  // this allows us to map columns to col widths
  const invertedData: FtCellObject[][] = unzip(dataCells);
  const widths: ColInfo[] = invertedData.map(
    (rows: FtCellObject[]): ColInfo => {
      // calculate maxWidth based on largest cell content in column
      const maxWidth = rows.reduce(
        (w: number, cell: FtCellObject) =>
          Math.max(w, cell?.v?.toString().length),
        minWidth
      );
      return { wch: maxWidth };
    }
  );
  logger.debug('col widths', widths);
  return widths;
};

@Injectable({ providedIn: 'root' })
export class ExcelDownloadService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private caveatService: CaveatService) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * get the proximal caveats for a particular placement
   */
  public getProximalCaveat$(placement: string): Observable<FtCellObject[][]> {
    return this.caveatService.getProximals$(placement).pipe(
      take(1), // unsubscribe after first result
      takeUntil(this.unsubscribe$), // or unsubscribe after destroy - whichever is first
      map((caveatContext: string[]): FtCellObject[][] => {
        const caveat: FtCellObject[][] = [];
        const caveatData: string[] = caveatContext
          .map((data) => data.split('\n'))
          .flat();

        caveatData?.forEach((data: string) => {
          let trimmedData = data.replace(/(<([^>]+)>)/gi, '');
          trimmedData = this.replaceWithAmpersand(trimmedData).trim();

          if (trimmedData.length > 0) {
            caveat.push([getCellObject(trimmedData, 's')]);
          }
        });

        return caveat;
      }),
      catchError(() => {
        return [];
      })
    );
  }

  private replaceWithAmpersand(data: string): string {
    const regex = /&amp;/gi;
    return data.replace(regex, '&');
  }
}
